import { Component, Vue } from 'vue-property-decorator';
import { DexieDataSetEvent } from '@/models/dexie/UnsyncedDexieDataSet';
import { DataSetEventTypeEnums } from '@/enums/DataSetEventTypeEnums';
import { ConfigurationField } from '@/models/configuration/Field';
import { ConfigurationSection } from '@/models/configuration/Section';
import { Helpers } from '@/helpers/Helpers';
import { DataSet } from '@/models/dataset/DataSet';
import { EventStore } from '@/store/store.event';
import { EventsService, MyMillDataService, SynchronizationService } from '@/services/index';
import { LoggerTypeEnum } from '@/enums/LoggerTypeEnums';
import { ApiIdentifers } from '@/config/index';
import { LoggerService } from '@/utils';
import { DataSetEditor } from '@/models/dataset/DataSetEditor';
import { ConfigurationService } from '@/services/index';
import { QuickEditFieldModel } from '@/models/quickEdit/QuickEditField';
import { QuickEditService, AppSettingsService } from '@/services/index';

import { ISectionComponentMethods } from '@/components/edit/section/Section';
import Section from '@/components/edit/section/Section.vue';
import HeaderBar from '@/components/general/headerbar/HeaderBar.vue';

import { DataSetSection } from '@/models/dataset/DataSetSection';
import { DataSetField } from '@/models/dataset/DataSetField';
import { Configuration } from '@/models/configuration/Configuration';

@Component({
    components: {
        HeaderBar,
        Section,
    },
})
export default class MyMillWizardComponent extends Vue {
    public epindex: number = 0;
    public epIndizes: number[] = [0];
    public isReadonly: boolean = true;
    public saveButtonActive: boolean = true;

    public dialog: boolean = false;
    public dialogHeadline: string = '';
    public dialogText: string = '';
    public dialogCancel: string = '';
    public dialogOk: string = '';
    public dialogCheckbox: boolean = false;
    public dialogCheckboxShow: boolean = false;
    public dialogCheckboxText: string = '';
    public closeForm: boolean = false;

    public creator?: DataSetEditor = new DataSetEditor();
    public editor?: DataSetEditor = new DataSetEditor();
    public showInfo: boolean = false;

    public quickCopyToast: boolean = false;
    public quickCopyToastTimeout: number = 3000;
    public quickCopyToastText: string = '';

    public mymillEditConfigurationSection: ConfigurationSection[] = [];

    private unsyncedDataSetId?: number;

    // tslint:disable-next-line: no-empty
    public dialogCallbackOK = () => {};

    public beforeMount() {
        this.createSectionsForMyMillWizard();
        this.mymillEditConfigurationSection = ConfigurationService.GetUsedConfigurationSections;
    }

    public async mounted() {
        this.unsyncedDataSetId = parseInt(this.$route.params.unsyncedId, 10) || undefined;

        if (this.unsyncedDataSetId) {
            // TODO: edit unsynced dataSet
            const data = EventStore.GetUnsyncedEvents.find((ur) => ur.id === this.unsyncedDataSetId);
            if (data !== undefined) {
                this.setDataSections(data.dataSet.sections);
            }
        }
    }

    public beforeDestroy() {
        ConfigurationService.ClearUsedConfigurationSections();
        MyMillDataService.SetSelectedUnit();
    }

    get getUsedQuickEditConfigurationSections() {
        return QuickEditService.usedQuickEditConfigurationSections;
    }

    public onOpenRequest(sectionIndex: number) {
        const openIndex = this.epIndizes.findIndex((x) => x === sectionIndex);

        LoggerService.LogDebug(`onOpenRequest: ${sectionIndex} | ${this.epindex}`);

        if (openIndex > -1) {
            if (this.checkOpenSection(sectionIndex) === true) {
                this.epIndizes.splice(openIndex, 1);
            }
        } else {
            this.epindex = sectionIndex;
            this.epIndizes.push(sectionIndex);
        }
    }

    public onClick(sectionIndex: number) {
        LoggerService.LogDebug(`Section ${this.mymillEditConfigurationSection[sectionIndex].name} clicked: ${sectionIndex}`);
        this.onOpenRequest(sectionIndex);
    }

    public onSaveClicked(close: boolean) {
        this.closeForm = close;
        if (this.checkAllSectionComponents() === false) {
            return;
        } else {
            /**
             * Check materials extra, for the blend check
             */
            const sectionIndex = this.mymillEditConfigurationSection.findIndex((sec) => sec.identifier === ApiIdentifers.material);
            if (sectionIndex > -1 && this.checkOpenSection(sectionIndex) === false) {
                return;
            } else if (this.checkAllSections() === true) {
                this.dialogHeadline = this.$vuetify.lang.t('$vuetify.edit.dialog.saveMyMill.title');
                this.dialogText = this.$vuetify.lang.t('$vuetify.edit.dialog.saveMyMill.text');
                this.dialogCancel = this.$vuetify.lang.t('$vuetify.buttons.cancel');
                this.dialogOk = this.$vuetify.lang.t('$vuetify.buttons.save');
                this.dialogCheckboxShow = false;

                this.dialogCallbackOK = () => {
                    this.dialog = false;
                    this.dialogCheckboxShow = false;
                    this.SaveSections(this.dialogCheckbox);
                };

                this.dialog = true;
            }
        }
    }

    public onCancelClicked() {
        this.dialogHeadline = this.$vuetify.lang.t('$vuetify.edit.dialog.cancel.title');
        this.dialogText = this.$vuetify.lang.t('$vuetify.edit.dialog.cancel.text');
        this.dialogCancel = this.$vuetify.lang.t('$vuetify.buttons.cancel');
        this.dialogOk = this.$vuetify.lang.t('$vuetify.buttons.ok');

        this.dialogCallbackOK = () => {
            this.dialog = false;
            this.$router.back();
        };

        this.dialog = true;
    }

    /**
     * Method to fetch translation with fallback
     * @param section ConfigurationSection
     */
    public getIdentifierText(section: ConfigurationSection): string {
        let ret: string = '';
        ret = this.$vuetify.lang.t(`$vuetify.identifiers.${section.identifier}`);
        // fallback to name string if no translation found
        if (ret === undefined || ret.length === 0 || ret === section.identifier || ret.includes('$vuetify.identifiers.')) {
            ret = section.name;
            LoggerService.LogDebug(`getIdentifierText fallback text for: ${section.identifier} --> ${ret}`);
        }
        return ret;
    }

    private removeInnerSubSectionFields(allFields: ConfigurationField[] | undefined, fieldsToAdd: QuickEditFieldModel[] | undefined, parentIdentifier: string) {
        if (allFields && fieldsToAdd) {
            const fieldsToRemove: ConfigurationField[] = [];
            allFields.forEach((field) => {
                let fieldIndex = fieldsToAdd.findIndex((f) => f.identifier === field.identifier);
                if (fieldIndex !== -1) {
                    this.removeInnerSubSectionFields(field.fields, fieldsToAdd[fieldIndex].fields, fieldsToAdd[fieldIndex].identifier);
                } else {
                    fieldIndex = allFields.findIndex((f) => f.identifier.includes(parentIdentifier));
                    if (fieldIndex !== -1) {
                        // for dynamic created sections with identifier + __count
                        this.removeInnerSubSectionFields(allFields[fieldIndex].fields, fieldsToAdd, parentIdentifier);
                    } else {
                        fieldsToRemove.push(field);
                    }
                }
            });
            for (let i = fieldsToRemove.length - 1; i >= 0; --i) {
                const index = allFields.findIndex((t) => t.identifier === fieldsToRemove[i].identifier);
                if (index !== -1) {
                    allFields.splice(index, 1);
                }
            }
        }
    }

    private getSectionRef(sectionRefName: string): ISectionComponentMethods & Section {
        return Helpers.getReference<ISectionComponentMethods & Section>(this.$refs, sectionRefName);
    }

    private async SaveSections(generatePdf: boolean) {
        // sort machines to have correct order in save data
        this.mymillEditConfigurationSection.sort((lhs, rhs) => lhs.position - rhs.position);
        const editDataSet = new DataSet({ sections: this.mymillEditConfigurationSection });

        /**
         * TODO: add send to server for mymill unit/machine
         */
        LoggerService.LogDebug(`MyMill Uunit Wizard save to BE not implemented yet. `);

        const eventType = DataSetEventTypeEnums.CREATE_MYMILL;
        await EventsService.PutUnsyncedEvent(
            new DexieDataSetEvent({
                dataSet: editDataSet,
                event: eventType,
                date: new Date().toISOString(),
            }),
        )
            .then(async (data: number) => {
                if (LoggerService.defaultLogLevel === LoggerTypeEnum.DEBUG) {
                    await EventsService.GetAllUnsyncedEvents().then((response: DexieDataSetEvent[]) => {
                        LoggerService.LogDebug(`GetAllUNsyncedEvents: `, response);
                    });
                }
                LoggerService.LogDebug(`Saved section as local unsynced dataSet number: ${data}`);
                await EventStore.SetUnsyncedEvents();

                if (SynchronizationService.IsRunnung() === true) {
                    /**
                     * only sync now if auto sync is running
                     * this only needed for debug mode so we can
                     * see unsynced events better even if online
                     */
                    SynchronizationService.SyncNow();
                }
                AppSettingsService.SetNavigateTo = 'dashboard';
                this.$router.push('/dashboard');
            })
            .catch((error: any) => {
                LoggerService.LogError(`Error on saving: `, error);
            });
    }

    private checkOpenSection(index?: number): boolean {
        const sectionRef = this.getSectionRef(`section_${index != null ? index : this.epindex}`);
        return sectionRef.validateSection();
    }

    private checkAllSectionComponents(): boolean {
        let allSectionsValid = true;
        const invalidSections: number[] = [];

        for (let index = 0; index < this.mymillEditConfigurationSection.length; index++) {
            const sectionRef = this.getSectionRef(`section_${index}`);
            let valid = false;
            if (sectionRef != null) {
                valid = sectionRef.validateSection();
            }
            if (valid === false) {
                this.epindex = index;
                invalidSections.push(index);
            }
        }

        if (invalidSections.length > 0) {
            this.epIndizes = invalidSections;
            allSectionsValid = false;
        }
        return allSectionsValid;
    }

    private checkAllSections(): boolean {
        let allSectionsValid = true;
        const invalidSections: number[] = [];

        const sectionCount = this.mymillEditConfigurationSection.length;
        // tslint:disable-next-line: prefer-for-of
        for (let sectionIndex = 0; sectionIndex < sectionCount; sectionIndex++) {
            const section = this.mymillEditConfigurationSection[sectionIndex];
            const ret: boolean = section.isValidSection();
            allSectionsValid = ret;
            if (allSectionsValid === false) {
                this.epindex = sectionIndex;
                invalidSections.push(sectionIndex);
            }
        }

        if (invalidSections.length > 0) {
            this.epIndizes = invalidSections;
            allSectionsValid = false;
        }

        return allSectionsValid;
    }

    private findSectionField(inField: ConfigurationField, inIdentififer: string): ConfigurationField | undefined {
        let searchField: ConfigurationField | undefined;
        if (inField.identifier === inIdentififer) {
            searchField = inField;
        } else if (inField.fields !== undefined) {
            for (const subField of inField.fields) {
                const subSearchField = this.findSectionField(subField, inIdentififer);
                if (subSearchField !== undefined) {
                    searchField = subSearchField;
                    break;
                }
            }
        }
        return searchField;
    }

    private openAllSections() {
        let i = 0;
        this.epIndizes = Array.from(new Array(this.mymillEditConfigurationSection.length), () => i++);
    }

    private createSectionsForMyMillWizard() {
        const myMillSections = new Configuration(ConfigurationService.GetMymillImportConfiguration);

        return ConfigurationService.SetUsedConfigurationSections(myMillSections.sections);
    }

    private setDataSections(inDataSetSections: DataSetSection[]): void {
        inDataSetSections.forEach((dataSetSection) => {
            const configSection = this.mymillEditConfigurationSection.find((cs) => cs.identifier === dataSetSection.identifier);
            if (configSection) {
                dataSetSection.fields.forEach((dsf) => {
                    const configSectionField = configSection.fields.find((csf) => csf.identifier === dsf.identifier);
                    if (configSectionField) {
                        this.setDataFields(dsf, configSectionField);
                    }
                });
            }
        });
    }

    private setDataFields(inDataField: DataSetField, inConfigField: ConfigurationField): void {
        if (inDataField.fields && inDataField.fields.length > 0) {
            inDataField.fields.forEach((dataField) => {
                if (inConfigField.fields) {
                    const configField = inConfigField.fields.find((cf) => cf.identifier === dataField.identifier);
                    if (configField) {
                        this.setDataFields(dataField, configField);
                    }
                }
            });
        } else {
            if (inDataField.valueBool != null) {
                inConfigField.valueBool = inDataField.valueBool;
            } else if (inDataField.valueNumber != null) {
                inConfigField.valueNumber = inDataField.valueNumber;
            } else if (inDataField.valueString != null) {
                inConfigField.valueString = inDataField.valueString;
            }
        }
    }
}
